import { EntityState, EntityUIQuery, getEntityType, getIDType, HashMap, QueryEntity } from '@datorama/akita';
import { Observable, OperatorFunction } from 'rxjs';
import { map } from 'rxjs/operators';
import { distinctUntilArrayItemChanged, distinctUntilObjectChanged } from '../shared/common/src';

export class SharedQueryEntity<T, S extends EntityState<T>, U = any> extends QueryEntity<S> {
  override ui: EntityUIQuery<U>; // UI state query (optional)

  constructor(store) {
    super(store);
    this.ui = new EntityUIQuery<U>(store);
  }

  // Active entity methods
  getActiveIdItem = () => this.getActiveId();
  selectActiveIdItem = () => this.selectActiveId();

  // --- Entity Methods ---

  getItem = (id: getIDType<S>) => this.getEntity(id);
  selectItem = (id: getIDType<S>) => this.selectEntity(id);
  getActiveItem = () => this.getActive();
  selectActiveItem = () => this.selectActive();
  selectManyItem = (ids: getIDType<S>[]) => this.selectMany(ids);
  getState = () => this.getValue();

  // Projected Entity Selectors
  selectProjectedItem = <R>(id: getIDType<S>, project: (entity: T) => R) => {
    return this.selectEntity<R>(id, entity => project(entity as T)).pipe(
      distinctUntilObjectChanged() as OperatorFunction<R, R>
    );
  };

  selectProjectedEntities<R>(project: (entity: T) => R, asObject: false): Observable<R[]>;
  selectProjectedEntities<R>(project: (entity: T) => R, asObject: true): Observable<HashMap<R>>;
  selectProjectedEntities<R>(project: (entity: T) => R, asObject: boolean): Observable<R[] | HashMap<R>> {
    return asObject
      ? this.selectAll({ asObject: true }).pipe(
          map(entities =>
            Object.keys(entities).reduce((acc, key) => {
              acc[key] = project(entities[key] ?? ({} as T));
              return acc;
            }, {} as HashMap<R>)
          ),
          distinctUntilObjectChanged()
        )
      : this.selectAll({ asObject: false }).pipe(
          map(entities => entities.map(project)),
          distinctUntilArrayItemChanged()
        );
  }

  // --- UI Methods ---

  getUIItem = (id: getIDType<U>) => this.ui.getEntity(id);
  selectUIItem = (id: getIDType<U>) => this.ui.selectEntity(id);

  // Projected UI Selectors
  selectProjectedUIItem = <R>(id: getIDType<U>, project: (entity: getEntityType<U>) => R) => {
    return this.ui
      .selectEntity<R>(id, entity => project(entity as getEntityType<U>))
      .pipe(distinctUntilObjectChanged() as OperatorFunction<R, R>);
  };

  selectProjectedUIEntities<R>(project: (entity: getEntityType<U>) => R, asObject: false): Observable<R[]>;
  selectProjectedUIEntities<R>(project: (entity: getEntityType<U>) => R, asObject: true): Observable<HashMap<R>>;
  selectProjectedUIEntities<R>(
    project: (entity: getEntityType<U>) => R,
    asObject: boolean
  ): Observable<R[] | HashMap<R>> {
    return asObject
      ? this.ui.selectAll({ asObject: true }).pipe(
          map(entities =>
            Object.keys(entities).reduce((acc, key) => {
              acc[key] = project(entities[key] ?? ({} as getEntityType<U>));
              return acc;
            }, {} as HashMap<R>)
          ),
          distinctUntilObjectChanged()
        )
      : this.ui.selectAll({ asObject: false }).pipe(
          map(entities => entities.map(project)),
          distinctUntilArrayItemChanged()
        );
  }
}
